import { getFCMToken, onForegroundMessage, requestNotificationPermission } from './config';
import { MessagePayload } from 'firebase/messaging';

export interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  tag?: string;
  data?: Record<string, any>;
  requireInteraction?: boolean;
  vibrate?: number[];
  url?: string;
}

class PushNotificationService {
  private token: string | null = null;
  private unsubscribeForeground: (() => void) | null = null;
  private onTokenChangeCallbacks: ((token: string | null) => void)[] = [];
  private onMessageCallbacks: ((payload: MessagePayload) => void)[] = [];

  /**
   * Initialize push notifications
   */
  async initialize(): Promise<string | null> {
    try {
      // Request permission first
      const permission = await requestNotificationPermission();

      console.log('[PushNotifications] Notification permission:', permission);
      
      if (permission !== 'granted') {
        console.warn('Notification permission not granted:', permission);
        return null;
      }

      // Register service worker
      await this.registerServiceWorker();

      // Get FCM token (this also initializes Firebase messaging)
      this.token = await getFCMToken();
      this.notifyTokenChange();
      
      if (!this.token) {
        console.warn('[PushNotifications] No FCM token obtained, skipping foreground listener setup');
        return null;
      }
      
      // Wait a bit to ensure messaging is fully initialized before setting up listener
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Set up foreground message listener with retry logic
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries && !this.unsubscribeForeground) {
        try {
          await this.setupForegroundListener();
          
          if (!this.unsubscribeForeground) {
            retries++;
            if (retries < maxRetries) {
              console.log(`[PushNotifications] Foreground listener setup failed, retrying (${retries}/${maxRetries})...`);
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        } catch (error) {
          console.error(`[PushNotifications] Error setting up foreground listener (attempt ${retries + 1}):`, error);
          retries++;
          if (retries < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      // Verify listener is set up
      if (this.unsubscribeForeground) {
        console.log('[PushNotifications] ✅ Foreground listener is active and ready to receive messages');
      } else {
        console.error('[PushNotifications] ❌ Foreground listener failed to set up after multiple attempts');
        console.error('[PushNotifications] This may indicate Firebase messaging is not properly initialized');
      }

      return this.token;
    } catch (error) {
      console.error('Error initializing push notifications:', error);
      return null;
    }
  }

  /**
   * Register service worker for background notifications
   */
  private async registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
    if (typeof window === 'undefined') {
      console.warn('[PushNotifications] Cannot register service worker: window is undefined');
      return null;
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('[PushNotifications] Service workers are not supported in this browser');
      const userAgent = typeof navigator !== 'undefined' && 'userAgent' in navigator ? (navigator as Navigator).userAgent : 'N/A';
      console.warn('[PushNotifications] User Agent:', userAgent);
      return null;
    }

    try {
      // Check if we're on localhost or https
      // Allow localhost, 127.0.0.1, ::1 (IPv6), 0.0.0.0, and any hostname ending in .local
      const hostname = location.hostname;
      const isLocalhost = 
        hostname === 'localhost' || 
        hostname === '127.0.0.1' || 
        hostname === '::1' ||
        hostname === '0.0.0.0' ||
        hostname.endsWith('.local') ||
        hostname.startsWith('localhost:') ||
        hostname.startsWith('127.0.0.1:');
      
      const isSecure = location.protocol === 'http:' || location.protocol === 'https:';
      
      // Always allow localhost (even without HTTPS) and HTTPS connections
      if (!isSecure && !isLocalhost) {
        console.warn('[PushNotifications] Service workers require HTTPS (except on localhost)');
        console.warn('[PushNotifications] Current protocol:', location.protocol);
        console.warn('[PushNotifications] Current hostname:', hostname);
        return null;
      }

    const userAgent = 'userAgent' in navigator ? (navigator as Navigator).userAgent : 'N/A';
    console.log('[PushNotifications] Registering service worker...', {
      protocol: location.protocol,
      hostname: location.hostname,
      isLocalhost,
      isSecure,
      userAgent: userAgent,
    });
    
    if (isLocalhost) {
      console.log('[PushNotifications] ✅ Localhost detected - Service worker allowed without HTTPS');
    }

      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
      });

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;

      // Inject Firebase config into service worker
      const { getFirebaseConfig } = await import('./config');
      const config = await getFirebaseConfig();
      
      // Send config to active service worker
      if (registration.active) {
        registration.active.postMessage({
          type: 'FIREBASE_CONFIG',
          config: config,
        });
        console.log('[PushNotifications] Firebase config injected into active service worker');
      }
      
      // Also send to installing/installed service worker if present
      if (registration.installing) {
        registration.installing.addEventListener('statechange', (event) => {
          const worker = event.target as ServiceWorker | null;
          if (worker && worker.state === 'activated' && 'postMessage' in worker) {
            worker.postMessage({
              type: 'FIREBASE_CONFIG',
              config: config,
            });
            console.log('[PushNotifications] Firebase config injected into newly activated service worker');
          }
        });
      }

      // Listen for service worker updates to re-inject config
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (newWorker) {
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'activated' && 'postMessage' in newWorker) {
              newWorker.postMessage({
                type: 'FIREBASE_CONFIG',
                config: config,
              });
              console.log('[PushNotifications] Firebase config injected into updated service worker');
            }
          });
        }
      });

      console.log('[PushNotifications] ✅ Service Worker registered:', registration);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }

  /**
   * Set up foreground message listener
   */
  private async setupForegroundListener(): Promise<void> {
    // Check if in browser environment
    if (typeof window === 'undefined') {
      console.warn('[PushNotifications] Cannot setup foreground listener: Not in browser environment');
      return;
    }

    // Cleanup existing listener if any
    if (this.unsubscribeForeground) {
      console.log('[PushNotifications] Cleaning up existing foreground listener');
      this.unsubscribeForeground();
      this.unsubscribeForeground = null;
    }

    console.log('[PushNotifications] Setting up foreground message listener...');

    try {
      // onForegroundMessage will handle checking if messaging is available
      const unsubscribe = await onForegroundMessage((payload) => {
        console.log('[PushNotifications] ✅ Foreground message received and processed:', {
          title: payload.notification?.title || payload.data?.title,
          messageId: payload.messageId,
        });
        
        // Notify all registered callbacks
        if (this.onMessageCallbacks && this.onMessageCallbacks.length > 0) {
          this.onMessageCallbacks.forEach((callback) => {
            try {
              callback(payload);
            } catch (callbackError) {
              console.error('[PushNotifications] Error in foreground message callback:', callbackError);
            }
          });
        } else {
          console.log('[PushNotifications] No callbacks registered for foreground messages');
        }
      });

      if (unsubscribe) {
        this.unsubscribeForeground = unsubscribe;
        console.log('[PushNotifications] ✅ Foreground message listener set up successfully');
      } else {
        console.warn('[PushNotifications] ⚠️ Failed to set up foreground message listener - messaging may not be initialized yet');
      }
    } catch (error) {
      console.error('[PushNotifications] ❌ Error setting up foreground message listener:', error);
    }
  }

  /**
   * Get current FCM token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Refresh FCM token
   */
  async refreshToken(): Promise<string | null> {
    this.token = await getFCMToken();
    this.notifyTokenChange();
    return this.token;
  }

  /**
   * Subscribe to token changes
   */
  onTokenChange(callback: (token: string | null) => void): () => void {
    this.onTokenChangeCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.onTokenChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.onTokenChangeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Subscribe to incoming messages
   */
  onMessage(callback: (payload: MessagePayload) => void): () => void {
    this.onMessageCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.onMessageCallbacks.indexOf(callback);
      if (index > -1) {
        this.onMessageCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Notify all token change callbacks
   */
  private notifyTokenChange(): void {
    this.onTokenChangeCallbacks.forEach((callback) => {
      try {
        callback(this.token);
      } catch (error) {
        console.error('Error in token change callback:', error);
      }
    });
  }

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    // Double check we're in browser environment
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      console.warn('[PushNotifications] Not in browser environment (window or navigator undefined)');
      return false;
    }

    // Check each feature individually with detailed logging
    const hasNavigator = typeof navigator !== 'undefined';
    const hasServiceWorker = hasNavigator && 'serviceWorker' in navigator;
    const hasWindow = typeof window !== 'undefined';
    const hasPushManager = hasWindow && 'PushManager' in window;
    const hasNotification = hasWindow && 'Notification' in window;

    // Debug logging
    console.log('[PushNotifications] Browser support check:', {
      hasNavigator,
      hasWindow,
      hasServiceWorker,
      hasPushManager,
      hasNotification,
      userAgent: hasNavigator ? navigator.userAgent : 'N/A',
      protocol: hasWindow ? window.location.protocol : 'N/A',
      hostname: hasWindow ? window.location.hostname : 'N/A',
    });

    if (!hasNavigator) {
      console.warn('[PushNotifications] navigator object not available');
    }
    if (!hasWindow) {
      console.warn('[PushNotifications] window object not available');
    }
    if (!hasServiceWorker) {
      console.warn('[PushNotifications] Service Worker not supported in this browser');
    }
    if (!hasPushManager) {
      console.warn('[PushNotifications] PushManager not supported in this browser');
    }
    if (!hasNotification) {
      console.warn('[PushNotifications] Notification API not supported in this browser');
    }

    const isSupported = hasServiceWorker && hasPushManager && hasNotification;

    if (isSupported) {
      console.log('[PushNotifications] ✅ Browser supports push notifications');
    } else {
      console.warn('[PushNotifications] ❌ Browser does not support push notifications', {
        hasServiceWorker,
        hasPushManager,
        hasNotification,
        userAgent: hasNavigator ? navigator.userAgent : 'N/A',
        protocol: hasWindow ? window.location.protocol : 'N/A',
      });
    }
    
    return isSupported;
  }

  /**
   * Check notification permission status
   */
  getPermissionStatus(): NotificationPermission | null {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return null;
    }
    
    return Notification.permission;
  }

  /**
   * Cleanup
   */
  cleanup(): void {
    if (this.unsubscribeForeground) {
      this.unsubscribeForeground();
      this.unsubscribeForeground = null;
    }
    
    this.onTokenChangeCallbacks = [];
    this.onMessageCallbacks = [];
  }

  /**
   * Debug: Check browser support details
   */
  debugBrowserSupport(): {
    hasWindow: boolean;
    hasNavigator: boolean;
    hasServiceWorker: boolean;
    hasPushManager: boolean;
    hasNotification: boolean;
    protocol: string | undefined;
    hostname: string | undefined;
    isLocalhost: boolean;
    isSecure: boolean;
    userAgent: string;
    isInIframe: boolean;
  } | void {
    if (typeof window === 'undefined') {
      console.log('[PushNotifications Debug] Running on server-side');
      return;
    }

    const hostname = window.location?.hostname || '';
    const isLocalhost = 
      hostname === 'localhost' || 
      hostname === '127.0.0.1' || 
      hostname === '::1' ||
      hostname === '0.0.0.0' ||
      hostname.endsWith('.local') ||
      hostname.startsWith('localhost:') ||
      hostname.startsWith('127.0.0.1:');

    const checks = {
      hasWindow: typeof window !== 'undefined',
      hasNavigator: typeof navigator !== 'undefined',
      hasServiceWorker: typeof navigator !== 'undefined' && 'serviceWorker' in navigator,
      hasPushManager: typeof window !== 'undefined' && 'PushManager' in window,
      hasNotification: typeof window !== 'undefined' && 'Notification' in window,
      protocol: window.location?.protocol,
      hostname: hostname,
      isLocalhost: isLocalhost,
      isSecure: window.location?.protocol === 'https:',
      userAgent: typeof navigator !== 'undefined' && 'userAgent' in navigator ? (navigator as Navigator).userAgent : 'N/A',
      isInIframe: window.self !== window.top,
    };

    console.log('[PushNotifications Debug] Browser Support Check:', checks);
    console.log('[PushNotifications Debug] Is Supported:', this.isSupported());
    
    return checks;
  }

  /**
   * Check if message handlers are properly set up
   */
  checkMessageHandlers(): void {
    if (typeof window === 'undefined') {
      console.log('[PushNotifications Debug] Running on server-side');
      return;
    }

    console.log('[PushNotifications Debug] ========== Message Handlers Status ==========');
    
    // Check foreground listener
    if (this.unsubscribeForeground) {
      console.log('[PushNotifications Debug] ✅ Foreground message listener is set up');
    } else {
      console.warn('[PushNotifications Debug] ⚠️ Foreground message listener is NOT set up');
    }

    // Check service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log('[PushNotifications Debug] ✅ Service worker is registered');
        console.log('[PushNotifications Debug] Service worker state:', registration.active?.state);
        
        // Check if service worker has Firebase config
        registration.active?.postMessage({
          type: 'CHECK_STATUS',
        });
      }).catch((error) => {
        console.error('[PushNotifications Debug] ❌ Service worker error:', error);
      });
    } else {
      console.warn('[PushNotifications Debug] ⚠️ Service workers not supported');
    }

    // Check notification permission
    if ('Notification' in window) {
      console.log('[PushNotifications Debug] Notification permission:', Notification.permission);
    } else {
      console.warn('[PushNotifications Debug] ⚠️ Notification API not available');
    }

    // Check Firebase messaging
    import('./config').then(({ getFirebaseMessaging }) => {
      getFirebaseMessaging().then((messaging) => {
        if (messaging) {
          console.log('[PushNotifications Debug] ✅ Firebase messaging instance is available');
        } else {
          console.warn('[PushNotifications Debug] ⚠️ Firebase messaging instance is NOT available');
        }
      });
    });

    console.log('[PushNotifications Debug] Registered callbacks:', this.onMessageCallbacks.length);
    console.log('[PushNotifications Debug] Current token:', this.token ? `${this.token.substring(0, 20)}...` : 'null');
    
    // Check if service worker is active and has Firebase initialized
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        console.log('[PushNotifications Debug] Service worker registrations:', registrations.length);
        registrations.forEach((registration, index) => {
          console.log(`[PushNotifications Debug] Registration ${index + 1}:`, {
            active: registration.active?.state,
            installing: registration.installing?.state,
            waiting: registration.waiting?.state,
            scope: registration.scope,
          });
        });
      });
    }
    
    console.log('[PushNotifications Debug] ============================================');
  }
  
  /**
   * Verify push notification setup is complete
   */
  verifySetup(): void {
    if (typeof window === 'undefined') {
      console.log('[PushNotifications] Cannot verify setup on server-side');
      return;
    }

    console.log('[PushNotifications] ========== Setup Verification ==========');
    console.log('[PushNotifications] Token:', this.token ? '✅ Set' : '❌ Not set');
    console.log('[PushNotifications] Foreground listener:', this.unsubscribeForeground ? '✅ Active' : '❌ Not active');
    console.log('[PushNotifications] Registered callbacks:', this.onMessageCallbacks.length);
    console.log('[PushNotifications] Notification permission:', Notification.permission);
    console.log('[PushNotifications] Service worker support:', 'serviceWorker' in navigator);
    console.log('[PushNotifications] Push manager support:', 'PushManager' in window);
    
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log('[PushNotifications] Service worker ready:', registration.active?.state);
        registration.active?.postMessage({ type: 'CHECK_STATUS' });
      });
    }
    
    console.log('[PushNotifications] ========================================');
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();

// Expose debug functions to window for console debugging
if (typeof window !== 'undefined') {
  (window as any).debugPushNotifications = () => {
    return pushNotificationService.debugBrowserSupport();
  };
  
  (window as any).checkMessageHandlers = () => {
    pushNotificationService.checkMessageHandlers();
  };
  
  (window as any).verifyPushSetup = () => {
    pushNotificationService.verifySetup();
  };
  
  console.log('[PushNotifications] Debug functions available:');
  console.log('  - window.debugPushNotifications() - Check browser support');
  console.log('  - window.checkMessageHandlers() - Check message handlers status');
  console.log('  - window.verifyPushSetup() - Verify complete setup status');
}

export default pushNotificationService;

